import { useState, useEffect, useCallback } from 'react';
import { KeyboardState } from '../types';

/**
 * Custom hook for tracking keyboard input state
 * 
 * Manages ArrowUp and ArrowDown key states with proper cleanup
 * and key repeat prevention.
 * 
 * @returns Keyboard state object with upPressed and downPressed booleans
 */
export function useKeyboardInput(): KeyboardState {
    const [keyState, setKeyState] = useState<KeyboardState>({
        upPressed: false,
        downPressed: false,
    });

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        // Prevent key repeat events
        if (event.repeat) return;

        switch (event.key) {
            case 'ArrowUp':
                event.preventDefault();
                setKeyState(prev => ({ ...prev, upPressed: true }));
                break;
            case 'ArrowDown':
                event.preventDefault();
                setKeyState(prev => ({ ...prev, downPressed: true }));
                break;
        }
    }, []);

    const handleKeyUp = useCallback((event: KeyboardEvent) => {
        switch (event.key) {
            case 'ArrowUp':
                event.preventDefault();
                setKeyState(prev => ({ ...prev, upPressed: false }));
                break;
            case 'ArrowDown':
                event.preventDefault();
                setKeyState(prev => ({ ...prev, downPressed: false }));
                break;
        }
    }, []);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [handleKeyDown, handleKeyUp]);

    return keyState;
}
